import { FastlyImage } from '@components/common/image'
import CommonCarousel from '@components/common/carousel'

export type StudentImpactStory = {
  image: string
  name: string
  grade: string
  country: string
  title: string
  description: string
  bgColor: string
}

const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 1280 },
    items: 2,
    slidesToSlide: 2,
  },
  desktop: {
    breakpoint: { max: 1279, min: 768 },
    items: 1,
  },
  tablet: {
    breakpoint: { max: 767, min: 640 },
    items: 2,
  },
  mobile: {
    breakpoint: { max: 639, min: 0 },
    items: 1,
  },
}

export const STUDENT_IMPACT_STORIES: StudentImpactStory[] = [
  {
    image: '/images/about-us/impact/rahib.jpg',
    name: 'Rahib Mujtahid',
    grade: 'Grade 4',
    country: 'Bangladesh',
    title: 'Consistency and curiosity built over hundred creative projects',
    description:
      "Rahib's curiosity drives him to keep creating. Over time, he built more than 100 Scratch projects, each reflecting experimentation and learning. His journey proves that consistent effort matters more than perfection. Celebrating small wins along the way helped him grow steadily while enjoying the process of learning.",
    bgColor: 'bg-green-100',
  },
  {
    image: '/images/about-us/impact/debashi.jpg',
    name: 'Debashi Das',
    grade: 'Grade 5',
    country: 'India',
    title: 'A simple coded gift sparked creativity and self belief',
    description:
      "Debashi's turning point came with a small but meaningful project, a coded heart for her mother. That experience helped her realize coding can be personal and expressive. The project boosted her confidence and sparked a love for creating things that matter emotionally, proving learning becomes powerful when connected to real feelings.",
    bgColor: 'bg-blue-200',
  },
  {
    image: '/images/about-us/impact/ayaan.jpg',
    name: 'Ayaan Khan',
    grade: 'Grade 7',
    country: 'India',
    title: 'Blending games and books to express creative thinking',
    description:
      'Ayaan combines creativity with logical thinking in unique ways. He built a 3D Pacman game using Scratch and AI and also published a children’s book. His journey shows how coding can inspire creativity beyond screens, encouraging students to express ideas through multiple formats while building confidence in technology and storytelling.',
    bgColor: 'bg-violet-100',
  },
  {
    image: '/images/about-us/impact/aaryav.jpg',
    name: 'Aaryav Sher',
    grade: 'Grade 8',
    country: 'Dubai',
    title: 'Using AI tools to turn creativity into interaction',
    description:
      'Aaryav explored AI-driven creativity by building an interactive website using Lovable. He enjoyed experimenting, testing ideas, and refining user interactions. Through this process, he learned how AI can enhance creativity rather than replace it. His journey shows how curiosity and experimentation help students confidently transform ideas into engaging digital experiences.',
    bgColor: 'bg-yellow-200',
  },
  {
    image: '/images/about-us/impact/kshitij-sodani.jpg',
    name: 'Kshitij Sodani',
    grade: 'Grade 12',
    country: 'India',
    title: 'From Olympiad gold to MIT, patience powered global excellence',
    description:
      'Kshitij’s journey reflects what long-term curiosity and discipline can achieve. From excelling in national competitions to winning Gold at the International Olympiad in Informatics, his progress has been remarkable. A two-time HPE CodeWars India winner and now a student at MIT, his story inspires learners to aim globally through consistency and focused learning.',
    bgColor: 'bg-orange2-400',
  },
  {
    image: '/images/about-us/impact/zaayan.jpg',
    name: 'Zaayan',
    grade: 'Grade 8',
    country: 'Bangladesh',
    title: 'Curiosity carried him from classrooms to Barcelona silver success',
    description:
      'Zaayan’s love for problem-solving took him far beyond the classroom. With focused preparation and growing confidence, he represented himself internationally and won Silver at the International STEM Olympiad in Barcelona. The experience strengthened his self-belief and showed how curiosity, when nurtured well, can lead to global recognition.',
    bgColor: 'bg-orange',
  },
  {
    image: '/images/about-us/impact/abreeq-ayaan.jpg',
    name: 'Abreeq Ayaan',
    grade: 'Grade 3',
    country: 'Bangladesh',
    title:
      'Grade three curiosity turned robotics passion into global confidence',
    description:
      'Abreeq’s fascination with how things work began early. With encouragement and structured learning, he explored robotics and confidently showcased his ideas at an international competition in Japan. Explaining his work with excitement showed that early exposure builds courage, communication skills, and confidence beyond technical learning.',
    bgColor: 'bg-grey-800',
  },
  {
    image: '/images/about-us/impact/paras-tickoo.jpg',
    name: 'Paras Tickoo',
    grade: 'Grade 8',
    country: 'India',
    title:
      'From Scratch beginnings to AI innovation finalist milestone achieved',
    description:
      'Paras began his coding journey with simple Scratch projects. Over time, his learning evolved into AI concepts and real-world problem-solving. Becoming a Samsung Solve for Tomorrow finalist marked a key milestone, showing how steady progress and curiosity can turn learners into confident innovators.',
    bgColor: 'bg-red-200',
  },
  {
    image: '/images/about-us/impact/jassmitha.jpg',
    name: 'Jassmitha',
    grade: 'Grade 7',
    country: 'India',
    title: 'Consistency and creativity led to gold and growing confidence',
    description:
      'Jassmitha’s journey is powered by regular practice and creative thinking. Through experimentation, she strengthened her logical reasoning and problem-solving skills. Winning Gold at the Go Coderz Inter-School Coding Competition boosted her confidence and reinforced the value of consistency in learning.',
    bgColor: 'bg-yellow-200',
  },
  {
    image: '/images/about-us/impact/nivaan.jpg',
    name: 'Nivaan',
    grade: 'Grade 9',
    country: 'India',
    title: 'Focus and practice transformed effort into Olympiad victory',
    description:
      'With structured learning and regular practice, Nivaan developed strong computational thinking skills. His dedication paid off when he secured first place at the Computer Science Olympiad. The win motivated him to keep challenging himself and aim higher through focused effort.',
    bgColor: 'bg-green-200',
  },
  {
    image: '/images/about-us/impact/nisam-hang-limbu.jpg',
    name: 'Nisam Hang Limbu',
    grade: 'Grade 10',
    country: 'Nepal',
    title:
      'Perseverance and steady growth earned national excellence recognition',
    description:
      'Nisam showed steady commitment throughout his learning journey. By strengthening fundamentals and improving step by step, he stood out for perseverance as much as performance. His efforts were recognized with the Annual Excellence Award in AI & Coding Champion.',
    bgColor: 'bg-blue-300',
  },
  {
    image: '/images/about-us/impact/aryan-sharma.jpg',
    name: 'Aryan Sharma',
    grade: 'Grade 10',
    country: 'India',
    title: 'Exploring hardware and Python to build real systems',
    description:
      'Curious about how technology works behind the scenes, Aryan Sharma explored Python alongside hardware integration. Building a functional Home Assistant helped him understand systems thinking and gave him confidence to create real, working solutions from scratch.',
    bgColor: 'bg-orange-800',
  },
  {
    image: '/images/about-us/impact/hayan.jpg',
    name: 'Hayan',
    grade: 'Grade 8',
    country: 'India',
    title: 'Designing practical apps by thinking from user perspective',
    description:
      'Hayan enjoys building tools that make everyday tasks simpler. His Web Viewer App brings multiple platforms together into one interface. The project helped him learn practical design thinking by focusing on usability and real user needs.',
    bgColor: 'bg-green-300',
  },
  {
    image: '/images/about-us/impact/than-vireakseth.jpg',
    name: 'Than Vireakseth',
    grade: 'Grade 6',
    country: 'Canada',
    title: 'Exploring many platforms to discover strengths and interests',
    description:
      'Than enjoys building across Scratch, Thunkable, HTML, and Roblox. Creating apps, games, and websites across platforms helped him discover what excites him most and grow confidently through exploration.',
    bgColor: 'bg-violet-100',
  },
  {
    image: '/images/about-us/impact/alika-persaud.jpg',
    name: 'Alika Persaud',
    grade: 'Grade 7',
    country: 'Canada',
    title: 'From math anxiety to confidence through patient learning',
    description:
      'Alika once found math intimidating. With steady guidance and practice, she began understanding patterns and building confidence. Over time, math became a strength, showing how belief and patience can transform learning experiences.',
    bgColor: 'bg-orange-900',
  },
  {
    image: '/images/about-us/impact/husnain.jpg',
    name: 'Husnain',
    grade: 'Grade 9',
    country: 'India',
    title: 'Speed, accuracy, and focus shaped exceptional mental math skills',
    description:
      'Husnain’s numerical clarity and exceptional speed set him apart early in his learning journey. With structured practice and dedication, he developed remarkable accuracy in mental math. Being recognized as a Human Calculator further solidified his confidence and focus, proving that nurturing natural strengths through consistent practice leads to excellence.',
    bgColor: 'bg-red-200',
  },
  {
    image: '/images/about-us/impact/eva-evan-bhattarai.jpg',
    name: 'Eva & Evan Bhattarai',
    grade: 'Grade 5 and Grade 2',
    country: 'Nepal',
    title:
      'Balancing Python, sports, and creativity with thoughtful discipline',
    description:
      'Eva balances Python learning with sports and creative activities. She enjoys building small apps while managing her time carefully, showing how children can grow across multiple interests confidently. Evan enjoys building games using Game Lab, learning logic through playful engagement. Together, they show how curiosity and discipline go hand in hand.',
    bgColor: 'bg-blue-200',
  },
  {
    image: '/images/about-us/impact/azhaan-nisar-mir.jpg',
    name: 'Azhaan Nisar Mir',
    grade: 'Grade 5',
    country: 'India',
    title: 'Racing ahead in his coding journey',
    description:
      'Azhaan is moving ahead quickly in his coding journey through curiosity and enthusiasm. His progress shows how interest-led learning helps students build skills confidently at a young age.',
    bgColor: 'bg-green-100',
  },
  {
    image: '/images/about-us/impact/jairus.jpg',
    name: 'Jairus',
    grade: 'Grade 10',
    country: 'India',
    title: 'Teacher guidance transformed his coding journey',
    description:
      'With encouragement from his teacher at Codingal, Jairus’ coding journey took a strong turn. He has now earned three titles, including Python Game Developer, showing the impact of mentorship and support.',
    bgColor: 'bg-orange',
  },
  {
    image: '/images/about-us/impact/sakina-abbas.jpg',
    name: 'Sakina Abbas',
    grade: 'Grade 9',
    country: 'India',
    title: 'Quiet focus turned learning into real world digital creation',
    description:
      'Sakina completed the Web Development course, is mastering Data Science with Python, and published Glamify, a product website for her friend. Her journey shows how quiet focus and consistency can lead to meaningful real-world outcomes.',
    bgColor: 'bg-yellow-200',
  },
  {
    image: '/images/about-us/impact/aarav-valand.jpg',
    name: 'Aarav Valand',
    grade: 'Grade 9',
    country: 'India',
    title:
      'Joyful coding moments inspiring young learners through creative reels',
    description:
      'Aarav’s coding reels capture curiosity, fun, and creativity in action. His journey shows how sharing learning moments can inspire other young learners to explore coding confidently.',
    bgColor: 'bg-orange2-400',
  },
]

type StudentImpactCardProps = {
  story: StudentImpactStory
}

const StudentImpactCard = ({ story }: StudentImpactCardProps) => {
  return (
    <div className="flex flex-col md:flex-row border h-full items-center md:items-start">
      {/* Left side - Student image and info */}
      <div
        className={`bg-white p-2 max-w-48 flex flex-col items-center justify-center`}
      >
        <div className="mb-2 md:mb-4 overflow-hidden">
          <FastlyImage
            src={story.image}
            alt={story.name}
            width={179}
            height={281}
            className="w-full h-full object-cover"
          />
        </div>
        <p className="text-lg md:text-xl lg:text-2xl font-600 text-center mb-1 leading-none">
          {story.name}
        </p>
        <p className="text-sm md:text-base text-grey-800 text-center leading-none">
          {story.grade}, {story.country}
        </p>
      </div>

      {/* Right side - Impact story */}
      <div
        className={`flex-1 h-full ${story.bgColor} bg-opacity-50 p-3 flex flex-col justify-start`}
      >
        <h3 className="text-lg lg:text-xl font-600 mb-2 text-center md:text-left">
          {story.title}
        </h3>
        <p className="text-base lg:text-lg text-center md:text-left">
          {story.description}
        </p>
      </div>
    </div>
  )
}

const StudentImpact = () => {
  return (
    <div className="py-12 lg:py-16 px-5 container mx-auto">
      <div>
        {/* Section heading */}
        <div className="text-center mb-8">
          <h2 className="max-w-4xl mx-auto leading-tight text-2xl text-center text-grey lg:text-4xl font-600">
            Impact on students
          </h2>
          <p className="pt-3 text-lg text-center max-w-4xl mx-auto">
            Helping children build confidence, curiosity and real skills
          </p>
        </div>

        {/* Carousel */}
        <CommonCarousel responsive={responsive}>
          {STUDENT_IMPACT_STORIES.map((story, index) => (
            <div key={index} className="px-2 h-full">
              <StudentImpactCard story={story} />
            </div>
          ))}
        </CommonCarousel>
      </div>
    </div>
  )
}

export default StudentImpact
